import Vue from 'vue'
import Router from 'vue-router'
import axiosClient from 'axios'
import * as Sentry from '@sentry/vue'
import store from './store/store'
import HelpPage from './views/HelpPage.vue'
import PageNotFound from './views/PageNotFound.vue'
import ConfigService from './services/ConfigService'
import chapiClient from './utilities/chapiClient'
import logger from './utilities/logger'
import content from '../public/assets/content/copy.json'
import { setPageTitle, setPageCanonicalLink } from './utilities/pageMetaData'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/help/other-questions',
      name: 'other-questions',
      meta: {
        title: content.faq_section_title_text,
        canonicalPath: `${process.env.BASE_URL}/other-questions`
      },
      // route level code-splitting
      // this generates a separate chunk (other-questions.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "other-questions" */ './views/OtherQuestions.vue')
    },
    {
      path: '/help/report-content-concern',
      name: 'reportConcern',
      component: () => import(/* webpackChunkName: "reportConcern" */ './views/ReportConcern.vue'),
      props: true
    },
    {
      path: '/help/section/:id/:slug',
      name: 'section',
      component: () => import(/* webpackChunkName: "section" */ './views/ArticlesCategory.vue'),
      props: true
    },
    {
      path: '/help/article/:id/:slug?',
      name: 'article',
      component: () => import(/* webpackChunkName: "article" */ './views/ArticlePage.vue'),
      props: true
    },
    {
      path: '/help',
      name: 'help',
      meta: {
        title: content.page_title_text,
        canonicalPath: process.env.BASE_URL
      },
      component: HelpPage
    },
    {
      path: '*',
      name: 'PageNotFound',
      meta: {
        title: content.error_page_not_found_title,
        canonicalPath: `${process.env.BASE_URL}/page-not-found`
      },
      component: PageNotFound
    }
  ],
  scrollBehavior(to, from) {
    /**
     * Scroll to top of the page for Article and Articles Category
     * - Except when toggling contact actions e.g. url hash changes
     */
    if (from.name !== to.name && (to.name === 'article' || to.name === 'section')) {
      return {
        y: 0
      }
    }
    return null
  }
})

// Additional environment variables are set in Package scripts `./scripts/package.js`
Sentry.init({
  Vue,
  dsn: 'https://73be27665ef243398d68232d74f4516a@o408587.ingest.sentry.io/5912671',
  attachStacktrace: true,
  environment: `${process.env.VUE_APP_ENVIRONMENT}`,
  release: `${process.env.PACKAGE_VERSION}`,
  tracesSampleRate: 0.1
})

function logRouteChange(route) {
  // known router links will contain a name value
  if (route.name != null && route.name !== 'PageNotFound') {
    logger.info(`Navigation: ${route.path}`)
  } else {
    logger.warn(`Missing route: ${route.path}`)
  }
}

// CHAD-4592 - Temporary solution to see how often we see users with an auth cookie, but not a user token
function logAuthenticatedState() {
  if (ConfigService.chapiAuthMode() !== 'token') return
  const hasLoggedAuthenticatedState = 'has-logged-authenticated-state'
  const hasLoggedState = sessionStorage.getItem(hasLoggedAuthenticatedState) || false
  if (hasLoggedState) return

  axiosClient.get('/api/account/details').then(userDetailsResponse => {
    const userDetails = {
      globalUserId: userDetailsResponse.data.userData.globalUserId,
      userId: userDetailsResponse.data.userData.userId
    }

    chapiClient.getUserToken().then(userTokenResponse => {
      const hasUserDetails = userDetails.globalUserId && userDetails.userId

      // CHAD-4592
      if (hasUserDetails && userTokenResponse) {
        logger.info('User logged in by auth cookie, and has a user token', userDetails)
      } else if (hasUserDetails && !userTokenResponse) {
        logger.warn('User logged in by auth cookie, but does not have a user token', userDetails)
      } else if (!hasUserDetails && userTokenResponse) {
        logger.warn('User not logged in by auth cookie, but has a user token')
      } else {
        logger.info('User not logged in by auth cookie or user token')
      }

      sessionStorage.setItem(hasLoggedAuthenticatedState, true)
    })
  })
}

router.beforeEach((to, from, next) => {
  //  Handle page title
  // Article and Section (Article Categories) title is handled in the vue file respectively
  if (to.name !== 'article' && to.name !== 'section') {
    setPageTitle(to.meta.title)
  }
  /**
   * Set Page canonical url
   * Initial `/help` page load, has value set in index.html
   * Subsequent page navigation `from.name` and other than `/help`
   * will have link updated
   */
  if (from.name || to.name !== 'help') {
    const links = ConfigService.canonicalLinks()
    const canonical = links.find(l => to.path.includes(l.pathMatch))
    const link = canonical ? canonical.link : `${window.location.origin}${to.path}`
    setPageCanonicalLink(link)
  }
  logRouteChange(to)
  logAuthenticatedState()

  // set test consumer id on window to be used by PH
  if (ConfigService.chapiAuthMode() === 'test') {
    window.testConsumerId = to.query.testuser

    if (from.query.testuser !== to.query.testuser) {
      store.dispatch('testUserUpdated', to.query.testuser)
    }
  }

  // When navigating to `other-questions`, ensure the browser scrolls to the top of the page
  // - Except when user clicks "Can we help with something else?" on the Help page
  // - Except when user toggles the Contact Actions e.g. url hash changes
  if (from.name !== 'help' && from.name !== to.name) {
    window.scrollTo(0, 0)
  }

  // Analytics tracking
  // Reset `cx_article` tracking data
  if ((from.name === 'article' || from.name === 'section') && from.params.id !== to.params.id) {
    store.dispatch('snowplow/resetCxArticle')
  }
  next()
})

router.afterEach((to, from, failure) => {
  // Analytics tracking
  if (failure) {
    const data = { code: null, message: `${failure}` }
    router.app.$root.$trackSelfDescEvent('cx_error', [], data)
  }
  // TBC: Prevent Help Article Contact action toggle
  if (from.name !== to.name) {
    router.app.$root.$trackPageView(to.name)
  }
})

export default router
